import React, { createContext, useContext, useState, useCallback } from 'react';

interface StepValidation {
    isValid: boolean;
    errors?: string[];
}

interface StepperContextType {
    currentStep: number;
    stepValidation: Record<number, StepValidation>;
    canGoToStep: (step: number) => boolean;
    goToStep: (step: number) => void;
    goToNextStep: () => void;
    goToPreviousStep: () => void;
    markStepValid: (step: number, isValid: boolean, errors?: string[]) => void;
    isStepValid: (step: number) => boolean;
    canProceed: () => boolean;
    totalSteps: number;
}

const StepperContext = createContext<StepperContextType | undefined>(undefined);

interface StepperProviderProps {
    children: React.ReactNode;
    totalSteps: number;
    initialStep?: number;
}

export const StepperProvider: React.FC<StepperProviderProps> = ({
    children,
    totalSteps,
    initialStep = 0
}) => {
    const [currentStep, setCurrentStep] = useState(initialStep);
    const [stepValidation, setStepValidation] = useState<Record<number, StepValidation>>({});

    const canGoToStep = useCallback((step: number) => {
        if (step < 0 || step >= totalSteps) return false;
        if (step <= currentStep) return true; // Can always go back

        // To go forward, all previous steps must be valid
        for (let i = 0; i < step; i++) {
            if (!stepValidation[i]?.isValid) return false;
        }
        return true;
    }, [currentStep, stepValidation, totalSteps]);

    const goToStep = useCallback((step: number) => {
        if (canGoToStep(step)) {
            setCurrentStep(step);
        }
    }, [canGoToStep]);

    const goToNextStep = useCallback(() => {
        if (currentStep < totalSteps - 1 && stepValidation[currentStep]?.isValid) {
            setCurrentStep(prev => prev + 1);
        }
    }, [currentStep, totalSteps, stepValidation]);

    const goToPreviousStep = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    }, [currentStep]);

    const markStepValid = useCallback((step: number, isValid: boolean, errors: string[] = []) => {
        setStepValidation(prev => ({
            ...prev,
            [step]: { isValid, errors: errors.length > 0 ? errors : undefined }
        }));
    }, []);

    const isStepValid = useCallback((step: number) => {
        return stepValidation[step]?.isValid ?? false;
    }, [stepValidation]);

    const canProceed = useCallback(() => {
        return isStepValid(currentStep);
    }, [currentStep, isStepValid]);

    const value: StepperContextType = {
        currentStep,
        stepValidation,
        canGoToStep,
        goToStep,
        goToNextStep,
        goToPreviousStep,
        markStepValid,
        isStepValid,
        canProceed,
        totalSteps,
    };

    return (
        <StepperContext.Provider value={value}>
            {children}
        </StepperContext.Provider>
    );
};

export const useStepperContext = (): StepperContextType => {
    const context = useContext(StepperContext);
    if (!context) {
        throw new Error('useStepperContext must be used within a StepperProvider');
    }
    return context;
};
